import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSacredAudio } from '../hooks/useSacredAudio';
import { useRealtimeAudio } from '../hooks/useRealtimeAudio';
import { sacredFrequencyGenerator } from '../utils/sacredFrequencyGenerator';
import { storage } from '../utils/storageManager';
import { Music, Play, Volume2, Smartphone, Waves, Save, Folder } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Sacred Audio Harmony Lab Component
 * Embodying the harmony and balance of F=6
 * NOW WITH REAL-TIME DEVICE SENSOR INTEGRATION
 * 
 * - 4 sacred scale types (Fr00t, Solfeggio, Chakras, Golden Ratio)
 * - 5 sacred chord variations
 * - Real-time sensor-driven audio (reactive, generative, meditative modes)
 * - Auto-play preview system
 * - Device motion integration
 * 
 * - Clear volume control with dB display
 * - Audio initialization with user interaction
 * - Disable state when audio is playing (prevent overlaps)
 * - Optional auto-play (user choice)
 * - Graceful sensor permission handling
 * 
 */
const AudioHarmonyLab: React.FC = () => {
  const [selectedScale, setSelectedScale] = useState('fr00t');
  const [selectedChord, setSelectedChord] = useState('harmony');
  const [volume, setVolume] = useState(-20);
  const [autoPlay, setAutoPlay] = useState(true);
  const [realtimeMode, setRealtimeMode] = useState<'off' | 'reactive' | 'generative' | 'meditative'>('off');
  const [presetName, setPresetName] = useState('');
  const [showPresetSave, setShowPresetSave] = useState(false);
  
  const { playSacredScale, playSacredChord, playSacredTone, setVolume: setAudioVolume, isPlaying, initializeSacredAudio, isInitialized } = useSacredAudio();
  
  // Real-time audio with sensor integration
  const realtimeAudio = useRealtimeAudio({
    enabled: realtimeMode !== 'off',
    mode: realtimeMode === 'off' ? 'reactive' : realtimeMode as any,
    autoPlay: true,
    sensitivity: 0.7
  });

  // ⚖️ Astraea: Audio initializes on user interaction, not automatically
  // Components should check if audio is ready, but not auto-initialize
  useEffect(() => {
    // Just check if audio is ready, don't auto-initialize
    if (isInitialized) {
      console.log('🎵 AudioHarmonyLab: Audio ready');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  const sacredScales = [
    { id: 'fr00t', name: 'Fr00t Scale', description: 'F(432) R(528) 0(396) T(741) = 8(639)', color: '#8b5cf6' },
    { id: 'solfeggio', name: 'Solfeggio', description: 'Historical medieval scale', color: '#6366f1' },
    { id: 'chakras', name: 'Chakras', description: 'Energy center frequencies', color: '#10b981' },
    { id: 'golden', name: 'Golden Ratio', description: 'φ-based harmonics', color: '#f59e0b' },
  ];

  const sacredChords = [
    { id: 'fr00t', name: 'Fr00t Chord', description: 'F-R-T harmonic blend', color: '#8b5cf6' },
    { id: 'harmony', name: 'Harmony', description: 'Perfect balance tones', color: '#10b981' },
    { id: 'love', name: 'Love', description: 'Love-connection-expression', color: '#f43f5e' },
    { id: 'power', name: 'Power', description: 'Authority and strength', color: '#f59e0b' },
    { id: 'wisdom', name: 'Wisdom', description: 'Love-expression-divine', color: '#6366f1' },
  ];

  const handlePlayScale = async () => {
    // ✅ Initialize audio on user interaction
    await ensureAudioReady();
    // Use sacred frequency generator instead of basic implementation
    await sacredFrequencyGenerator.playScaleByName(selectedScale, 8);
    toast.success(`🎵 Playing ${sacredScales.find(s => s.id === selectedScale)?.name} scale`);
  };

  const handlePlayChord = async () => {
    // ✅ Initialize audio on user interaction
    await ensureAudioReady();
    // Use sacred frequency generator for real tones
    await sacredFrequencyGenerator.playChordByName(selectedChord, 4);
    toast.success(`🎶 Playing ${sacredChords.find(c => c.id === selectedChord)?.name} chord`);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    setAudioVolume(newVolume);
  };

  // Ensure audio is initialized before playing
  const ensureAudioReady = async () => {
    if (!isInitialized) {
      await initializeSacredAudio();
    }
  };

  // Auto-play when scale selection changes (if auto-play is enabled)
  const handleScaleSelect = async (scaleId: string) => {
    setSelectedScale(scaleId);
    if (autoPlay && !isPlaying) {
      await ensureAudioReady();
      // Short preview tone instead of full scale using sacred frequencies
      const scaleFreqs: { [key: string]: number } = {
        'fr00t': 432,    // A=432Hz
        'solfeggio': 528, // MI - Love frequency
        'chakras': 396,   // Root chakra
        'golden': 414.2   // 256 * phi
      };
      await sacredFrequencyGenerator.playTone({
        frequency: scaleFreqs[scaleId] || 432,
        duration: 0.6,
        volume: 0.25,
        waveform: 'sine'
      });
    }
  };

  // Auto-play when chord selection changes (if auto-play is enabled)
  const handleChordSelect = async (chordId: string) => {
    setSelectedChord(chordId);
    if (autoPlay && !isPlaying) {
      await ensureAudioReady();
      // Play the full chord automatically using sacred frequencies
      setTimeout(async () => {
        await sacredFrequencyGenerator.playChordByName(chordId, 2.5);
      }, 100);
    }
  };

  const cycleRealtimeMode = () => {
    const modes: Array<'off' | 'reactive' | 'generative' | 'meditative'> = ['off', 'reactive', 'generative', 'meditative'];
    const currentIndex = modes.indexOf(realtimeMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setRealtimeMode(nextMode);
    
    const modeNames = {
      'off': '🔇 Real-time audio disabled',
      'reactive': '⚡ Reactive mode: Sound from movement',
      'generative': '🌊 Generative mode: Continuous harmonics',
      'meditative': '🧘 Meditative mode: Stillness tones'
    };
    toast(modeNames[nextMode]);
  };

  return (
    <motion.div
      className="fr00t-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(30, 41, 59, 0.9)',
        borderColor: 'rgba(99, 102, 241, 0.3)'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1.5rem'
      }}>
        <Music size={24} color="#6366f1" />
        <h3 style={{ color: '#6366f1', fontSize: '1.25rem', fontWeight: '600' }}>
          Audio Harmony Lab
        </h3>
      </div>

      <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
        Sacred frequencies, mixing, and harmonic resonance
      </p>
      
      {/* Real-time Audio Mode */}
      <div style={{
        marginBottom: '1.5rem',
        padding: '0.75rem',
        background: realtimeMode !== 'off' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(100, 116, 139, 0.1)',
        borderRadius: '8px',
        border: `1px solid ${realtimeMode !== 'off' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(100, 116, 139, 0.2)'}`,
        cursor: 'pointer'
      }}
      onClick={cycleRealtimeMode}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Waves size={16} color={realtimeMode !== 'off' ? '#6366f1' : '#64748b'} />
            <span style={{ 
              color: realtimeMode !== 'off' ? '#6366f1' : '#64748b', 
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              {realtimeMode === 'off' && 'Real-Time Audio: Off'}
              {realtimeMode === 'reactive' && 'Reactive Mode'}
              {realtimeMode === 'generative' && 'Generative Mode'}
              {realtimeMode === 'meditative' && 'Meditative Mode'}
            </span>
          </div>
          {realtimeMode !== 'off' && realtimeAudio.movement.isMoving && (
            <span style={{ color: '#6366f1', fontSize: '0.75rem' }}>
              ♪ {Math.round(realtimeAudio.currentFrequency)}Hz
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.25rem' }}>
          {realtimeMode === 'off' && 'Click to enable sensor-driven audio'}
          {realtimeMode === 'reactive' && 'Plays tones in response to movement'}
          {realtimeMode === 'generative' && 'Continuous harmonic generation'}
          {realtimeMode === 'meditative' && 'Ambient tones during stillness'}
        </div>
      </div>

      {/* Quick Sensor Actions */}
      {realtimeMode !== 'off' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          <motion.button
            onClick={() => realtimeAudio.playFromCurrentSensors()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '0.5rem',
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid #6366f1',
              borderRadius: '6px',
              color: '#6366f1',
              fontSize: '0.65rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Play Now
          </motion.button>
          <motion.button
            onClick={() => realtimeAudio.playChakraFromMotion()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '0.5rem',
              background: 'rgba(139, 92, 246, 0.2)',
              border: '1px solid #8b5cf6',
              borderRadius: '6px',
              color: '#8b5cf6',
              fontSize: '0.65rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Chakra Tone
          </motion.button>
          <motion.button
            onClick={() => realtimeAudio.playHarmonicFromOrientation()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '0.5rem',
              background: 'rgba(16, 185, 129, 0.2)',
              border: '1px solid #10b981',
              borderRadius: '6px',
              color: '#10b981',
              fontSize: '0.65rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Harmonics
          </motion.button>
        </div>
      )}
      
      {/* Auto-play Toggle */}
      {realtimeMode === 'off' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          padding: '0.75rem',
          background: 'rgba(99, 102, 241, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(99, 102, 241, 0.2)'
        }}>
          <label style={{
            color: '#6366f1',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <input
              type="checkbox"
              checked={autoPlay}
              onChange={(e) => setAutoPlay(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Auto-play on selection
          </label>
          <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
            {autoPlay ? '🔊 Enabled' : '🔇 Disabled'}
          </span>
        </div>
      )}

      {/* Sacred Scales */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{
          display: 'block',
          color: '#6366f1',
          fontSize: '0.875rem',
          fontWeight: '600',
          marginBottom: '0.75rem'
        }}>
          Sacred Scales
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.5rem',
          marginBottom: '0.75rem'
        }}>
          {sacredScales.map(scale => (
            <motion.button
              key={scale.id}
              onClick={() => handleScaleSelect(scale.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '0.75rem',
                background: selectedScale === scale.id ? `${scale.color}20` : 'rgba(100, 116, 139, 0.1)',
                border: selectedScale === scale.id ? `2px solid ${scale.color}` : '2px solid rgba(100, 116, 139, 0.2)',
                borderRadius: '8px',
                color: selectedScale === scale.id ? scale.color : '#64748b',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <div style={{ marginBottom: '4px' }}>{scale.name}</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>{scale.description}</div>
            </motion.button>
          ))}
        </div>

        <motion.button
          onClick={handlePlayScale}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={isPlaying}
          style={{
            width: '100%',
            padding: '12px',
            background: 'rgba(99, 102, 241, 0.2)',
            border: '2px solid #6366f1',
            borderRadius: '8px',
            color: '#6366f1',
            fontWeight: '600',
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            opacity: isPlaying ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          <Play size={16} />
          Play Scale
        </motion.button>
      </div>

      {/* Sacred Chords */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{
          display: 'block',
          color: '#6366f1',
          fontSize: '0.875rem',
          fontWeight: '600',
          marginBottom: '0.75rem'
        }}>
          Sacred Chords
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.5rem',
          marginBottom: '0.75rem'
        }}>
          {sacredChords.map(chord => (
            <motion.button
              key={chord.id}
              onClick={() => handleChordSelect(chord.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '0.75rem',
                background: selectedChord === chord.id ? `${chord.color}20` : 'rgba(100, 116, 139, 0.1)',
                border: selectedChord === chord.id ? `2px solid ${chord.color}` : '2px solid rgba(100, 116, 139, 0.2)',
                borderRadius: '8px',
                color: selectedChord === chord.id ? chord.color : '#64748b',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <div style={{ marginBottom: '4px' }}>{chord.name}</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>{chord.description}</div>
            </motion.button>
          ))}
        </div>

        <motion.button
          onClick={handlePlayChord}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={isPlaying}
          style={{
            width: '100%',
            padding: '12px',
            background: 'rgba(99, 102, 241, 0.2)',
            border: '2px solid #6366f1',
            borderRadius: '8px',
            color: '#6366f1',
            fontWeight: '600',
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            opacity: isPlaying ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          <Play size={16} />
          Play Chord
        </motion.button>
      </div>

      {/* Volume Control */}
      <div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '0.5rem',
          fontSize: '0.875rem'
        }}>
          <label style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Volume2 size={16} />
            Volume
          </label>
          <span style={{ color: '#6366f1', fontWeight: '600' }}>{volume}dB</span>
        </div>
        <input
          type="range"
          min="-40"
          max="0"
          value={volume}
          onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
          style={{
            width: '100%',
            height: '8px',
            borderRadius: '4px',
            outline: 'none',
            background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(volume + 40) / 40 * 100}%, rgba(100, 116, 139, 0.3) ${(volume + 40) / 40 * 100}%, rgba(100, 116, 139, 0.3) 100%)`
          }}
        />
      </div>

      {/* Sacred Numerology */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'rgba(99, 102, 241, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '0.75rem',
          color: '#6366f1',
          fontFamily: 'monospace'
        }}>
          Sacred Audio • Solfeggio Frequencies • Harmonic Resonance
        </div>
      </div>
    </motion.div>
  );
};

export default AudioHarmonyLab;
